/* eslint-disable max-len */
import { VehicleSearchHistoryResponse } from '@1po/1po-bff-fe-spec/generated/catalog/search_vehicle/response/VehicleSearchHistoryResponse';
import { GetAvailableDMSServicesResponse } from '@1po/1po-bff-fe-spec/generated/dms/response/GetAvailableDMSServicesResponse';
import { GetUserSellersResponse } from '@1po/1po-bff-fe-spec/generated/user/response/GetUserSellersResponse';
import { PendingSellerWarningResponse } from '@1po/1po-bff-fe-spec/generated/user/response/PendingSellerWarningResponse';
import { SearchUserProfileResponse } from '@1po/1po-bff-fe-spec/generated/user/response/SearchUserProfileResponse';
import { UserNotificationResponse } from '@1po/1po-bff-fe-spec/generated/user/response/UserNotificationResponse';
import { UserNotificationsResponse } from '@1po/1po-bff-fe-spec/generated/user/response/UserNotificationsResponse';
import { call, put, takeEvery } from '@redux-saga/core/effects';

import { AnyAction } from 'redux';
import { SagaIterator } from 'redux-saga';
import { ISSUER_LOCAL_PARAM, LOGIN_METHOD_LOCAL_PARAM, SELECTED_PROFILE_PARAM } from 'domains/auth/Auth.types';
import { getAuthenticatedUser, getUserManager, mapUserFromOidcUser } from 'domains/auth/Auth.util';
import { setInitialState as setInitialBasketState, resetBasketSubscriptionStatus } from 'domains/basket/Basket.store';
import { fetchVehicleFunction } from 'domains/catalog/Catalog.requests';
import { getLastVehicleDetail, setInitialState as setInitialCatalogState } from 'domains/catalog/Catalog.store';
import { clearDelegationRestoreMarker } from 'domains/delegation/DelegationSession.restore';
import {
  delegationContextRefreshRequest,
  getActiveSession as getDelegationActiveSession,
} from 'domains/delegation/DelegationSession.store';
import { setInitialState as setInitialEPRState } from 'domains/electronicPartsRepair/ElectronicPartsRepair.store';
import {
  fetchEstimateByIdRequest,
  getCurrentEstimateId,
  resetEstimateSettingsForResubscription,
  setInitialState as setInitialEstimateState,
} from 'domains/estimate/Estimate.store';
import { setInitialState as setInitialGarageState } from 'domains/garage/Garage.store';
import { setInitialState as setInitialInformationState } from 'domains/information/Information.store';
import { setInitialState as setMaintenancePlanInitialState } from 'domains/maintenancePlan/MaintenancePlan.store';
import { setInitialState as setMaintenanceValuesState } from 'domains/maintenanceValues/MaintenanceValues.store';
import { setInitialState as setInitialOrderState, resetOrderSubscriptionStatus } from 'domains/order/Order.store';
import { setInitialState as setInitialOrderValidationState } from 'domains/orderValidation/OrderValidation.store';
import { setInitialState as setInitialPicturesState } from 'domains/pictures/Pictures.store';

import {
  setInitialState as setInitialPromotionState,
  setResubscribeableFieldsUndefined as setPromoResubscribeableFieldsUndefined,
} from 'domains/promotion/Promotion.store';
import {
  resetReferences,
  resetReferencesDiscounts,
  resetReferencesPriceData,
  setInitialState as setInitialReferencesState,
} from 'domains/references';
import { setInitialState as setInitialTimeState } from 'domains/time/Time.store';
import { setInitialState as setInitialTireState } from 'domains/tires/Tire.store';
import { setInitialState as setInitialSignatureState } from 'domains/urlSignatures/UrlSignatures.store';
import {
  addVehicleToSearchHistoryRequest,
  sendAddFavoriteTireBrandForUserRequest,
  sendCancelAllNotificationsRequest,
  sendCancelNotificationRequest,
  sendClearSearchHistoryRequest,
  sendFetchUserNotificationsRequest,
  sendGetAvailableDMSServicesRequest,
  sendGetUserProfileReconnectRequest,
  sendGetUserProfileRequest,
  sendGetUserSellersRequest,
  sendGetVehicleHistorySubscriptionRequest,
  sendIgnoreAllNotificationsRequest,
  sendIgnoreNotificationRequest,
  sendRemoveFavoriteTireBrandForUserRequest,
  sendRemoveVehicleFromSearchHistoryRequest,
  sendResetFavoriteTireBrandForUserRequest,
  sendUnsubscribeNotificationsRequest,
  sendUpdateDhCatalogReferenceViewRequestSaga,
  sendUpdateNotificationsOnEstimateExpirationRequest,
  sendUpdateNotificationsOnOrderStatusChangeRequest,
  sendUpdateNotificationsSettingsRequest,
  sendUpdateOrderValidationEmailAddressesRequest,
  sendUpdateOrderValidationEmailNotificationRequest,
  sendUpdateOrderValidationNotificationRequest,
  sendUpdateUserProfileLanguagesRequest,
  sendUpdateUserSellerRequest,
} from 'domains/user/User.api';
import { mapRoles, mapUserProfileResponse } from 'domains/user/User.mapper';
import * as actions from 'domains/user/User.store';
import {
  cancelAllUserNotifications,
  cancelUserNotification,
  getDocumentLanguage,
  getDocumentLanguageFallback,
  getIsReload,
  getUserCommercialLink,
  getUserProfileSearchStatus,
  getWebLanguage,
  initialState as userStoreInitialState,
  setAvailableDMSServices,
  setAvailableDMSServicesStatus,
  setCatalogReferenceView,
  setCommercialLink,
  setDocumentLanguages,
  setFavoriteTireBrandList,
  setInitialState,
  setPendingSellerWarning,
  setPendingSellerWarningStatus,
  setResubscribeableFieldsUndefined,
  setResubscribeableFieldsUndefinedForAppReconnection,
  setShowSellerPopupFalse,
  setUser,
  setUserIsLoading,
  setUserNotificationSettings,
  setUserNotificationsInit,
  setUserNotificationsUpdate,
  setUserProfileSearchStatus,
  setUserRoles,
  setUserSellersToChoose,
  setVehicleSearchHistory,
  setVehicleSearchHistoryStatus,
  setWebLanguage,
} from 'domains/user/User.store';
import { DEFAULT_COUNTRY } from 'domains/user/User.types';
import {
  getIsWsConnectInitialized,
  setWsConnectInitialized,
  setWsHasEverConnected,
  wsDisconnect,
} from 'domains/webSockets/WebSocket.store';
import { WsResponse } from 'domains/webSockets/WebSocket.types';
import { notifyTop } from 'UI/Notification/notification';
import { FOUND, LOADING, NOT_FOUND, sagaGuard, select } from 'utils';
import { endSagaWithSuccess, startSaga } from 'utils/domainStore';
import { AppTranslation, changeLanguage } from 'utils/i18n';
import { Language } from 'utils/i18n/Language';
import { logAndSetGTMLanguage, pushProductFruitsUserEvent } from 'utils/remoteLogger/remoteLogMetaData';

export function* setCurrentUserSaga(action: AnyAction): SagaIterator {
  yield call(startSaga, action);

  yield put(setUserIsLoading());
  const fetchedUser = yield call(getAuthenticatedUser);
  const user = fetchedUser && !fetchedUser.expired ? mapUserFromOidcUser(fetchedUser) : undefined;

  yield put(setUser(user));
  if (user?.id) {
    pushProductFruitsUserEvent(user.id);
  }
  yield call(endSagaWithSuccess, action, user);
}

export function* updateUserProfileLanguagesRequestSaga({
  payload,
}: ReturnType<typeof actions.updateUserProfileLanguagesRequestSaga>): SagaIterator {
  const { webLanguage, documentLanguage, documentLanguageFallback } = payload;
  yield put(sendUpdateUserProfileLanguagesRequest(webLanguage, documentLanguage, documentLanguageFallback));
}

export function* getUserProfileRequestSaga(): SagaIterator {
  const isWsConnectInitialized = yield* select(getIsWsConnectInitialized);
  const isReload = yield* select(getIsReload);
  if (!isWsConnectInitialized) {
    yield put(setUserProfileSearchStatus(LOADING));
    yield put(setPendingSellerWarningStatus(LOADING));
  }
  yield put(sendGetUserProfileRequest({ isLoggedIn: isReload }));
}

export function* getUserProfileReconnectSaga(): SagaIterator {
  yield put(sendGetUserProfileReconnectRequest());
}

export function* syncCommercialLinkAndDelegationContext(
  userProfile: ReturnType<typeof mapUserProfileResponse>,
  currentCommercialLink: ReturnType<typeof getUserCommercialLink>,
  isDelegationActive: boolean,
): SagaIterator {
  if (!userProfile?.commercialLink) {
    return;
  }

  // Profile push uses the token (R1) principal. While Connect-as is active the FE commercial
  // link must stay on the delegated buyer↔seller pair (synced from the delegation session).
  if (isDelegationActive) {
    return;
  }

  const hasCommercialLinkChanged =
    currentCommercialLink && currentCommercialLink?.seller?.id !== userProfile.commercialLink.seller?.id;

  if (hasCommercialLinkChanged) {
    yield call(reconnectUserToDifferentSeller);
  }

  yield put(setCommercialLink(userProfile.commercialLink));

  if (hasCommercialLinkChanged) {
    // Non-delegation seller change or delegation disconnect returning to original profile.
    yield put(delegationContextRefreshRequest());
  }
}

function getDocumentLanguageChange(
  userProfile: ReturnType<typeof mapUserProfileResponse>,
  docLangPrimary: ReturnType<typeof getDocumentLanguage>,
  docLangSecondary: ReturnType<typeof getDocumentLanguageFallback>,
): { documentLanguage: Language; documentLanguageFallback: Language } | undefined {
  if (!userProfile?.documentLanguage || !userProfile?.documentLanguageFallback) {
    return undefined;
  }

  const shouldChange =
    !Language.languagesAreEqual(docLangPrimary, userProfile.documentLanguage) ||
    !Language.languagesAreEqual(docLangSecondary, userProfile.documentLanguageFallback);

  if (!shouldChange) {
    return undefined;
  }

  return {
    documentLanguage: userProfile.documentLanguage,
    documentLanguageFallback: userProfile.documentLanguageFallback,
  };
}

export function* getUserProfileResponseSaga(action: WsResponse<SearchUserProfileResponse>): SagaIterator {
  const webLang = yield* select(getWebLanguage);
  const docLangPrimary = yield* select(getDocumentLanguage);
  const docLangSecondary = yield* select(getDocumentLanguageFallback);
  const searchStatus = yield* select(getUserProfileSearchStatus);
  const currentCommercialLink = yield* select(getUserCommercialLink);
  const activeDelegationSession = yield* select(getDelegationActiveSession);
  const userProfile = mapUserProfileResponse(action);

  const isDelegationActive = activeDelegationSession?.active === true;
  yield call(syncCommercialLinkAndDelegationContext, userProfile, currentCommercialLink, isDelegationActive);

  if (action.payload?.favoriteTireBrandList) {
    yield put(setFavoriteTireBrandList(action.payload.favoriteTireBrandList));
  }
  const userRoles = mapRoles(action.payload.roles);
  yield put(setUserRoles(userRoles));

  if (action.payload.catalogDisplay) {
    yield put(setCatalogReferenceView(action.payload.catalogDisplay));
  }

  if (userProfile?.webLanguage && !Language.languagesAreEqual(webLang, userProfile.webLanguage)) {
    yield put(actions.changeWebLanguageSaga(userProfile.webLanguage));
  }
  const documentLanguageChange = getDocumentLanguageChange(userProfile, docLangPrimary, docLangSecondary);
  if (documentLanguageChange) {
    yield put(
      actions.changeDocumentLanguageSaga({
        documentLanguage: documentLanguageChange.documentLanguage,
        documentLanguageFallback: documentLanguageChange.documentLanguageFallback,
      }),
    );
  }
  if (searchStatus !== FOUND) {
    yield put(setUserProfileSearchStatus(FOUND));
  }

  yield put(setUserNotificationSettings(userProfile?.userNotificationSettings));

  yield put(setWsConnectInitialized(true));
  yield put(setWsHasEverConnected(true));
}

export function* logoutUser(action: AnyAction): SagaIterator {
  yield call(startSaga, action);
  const webLanguageBeforeLogout = yield* select(getWebLanguage);

  getUserManager()
    .signoutRedirect()
    .finally(() => {
      // removeUser after signout otherwise got 400 error
      getUserManager().removeUser();
    });

  sessionStorage.removeItem(LOGIN_METHOD_LOCAL_PARAM);
  sessionStorage.removeItem(ISSUER_LOCAL_PARAM);
  sessionStorage.removeItem(SELECTED_PROFILE_PARAM);

  yield put(setInitialState());
  yield put(setInitialCatalogState());
  yield put(setInitialReferencesState());
  yield put(setInitialBasketState());
  yield put(setInitialEPRState());
  yield put(setInitialEstimateState());
  yield put(setInitialOrderValidationState());
  yield put(setInitialOrderState());
  yield put(setInitialGarageState());
  yield put(setInitialPicturesState());
  yield put(setInitialPromotionState());
  // todo: do we want this? or is it ok that after disconnect this will stay?
  yield put(setInitialTimeState());
  yield put(setInitialTireState());
  yield put(setInitialSignatureState());
  yield put(setMaintenancePlanInitialState());
  yield put(setMaintenanceValuesState());
  yield put(setWsConnectInitialized(false));
  yield put(setWsHasEverConnected(false));
  yield call(clearDelegationRestoreMarker);
  yield put(wsDisconnect());

  if (!Language.languagesAreEqual(webLanguageBeforeLogout, userStoreInitialState.webLanguage)) {
    yield put(actions.changeWebLanguageSaga(webLanguageBeforeLogout));
  }

  yield call(endSagaWithSuccess, action, null);
}

export function* disconnectUser(action: AnyAction): SagaIterator {
  yield call(startSaga, action);

  getUserManager().removeUser();
  yield put(wsDisconnect());
  yield call(endSagaWithSuccess, action, null);
}

function* reconnectUserToDifferentSeller() {
  yield put(setInitialCatalogState());
  yield put(setInitialPicturesState());
  yield put(setInitialReferencesState());
  yield put(setInitialBasketState());
  yield put(setInitialOrderValidationState());
  yield put(setInitialPromotionState());
  yield put(setInitialTireState());
  yield put(setMaintenancePlanInitialState());
  yield put(setMaintenanceValuesState());
  yield put(setResubscribeableFieldsUndefined());
}

export function* reloadSubscriptionBeUserSaga(): SagaIterator {
  const isWsConnectInitialized = yield* select(getIsWsConnectInitialized);

  // Always — these trigger subscription query resubscription, must run on both reconnect and first init
  yield put(resetBasketSubscriptionStatus());
  yield put(resetEstimateSettingsForResubscription());
  yield put(resetOrderSubscriptionStatus());
  yield put(setInitialEPRState());
  yield put(setPromoResubscribeableFieldsUndefined());

  const currentEstimateId = yield* select(getCurrentEstimateId);
  if (currentEstimateId) {
    yield put(fetchEstimateByIdRequest(currentEstimateId));
  }

  if (isWsConnectInitialized) {
    yield put(setResubscribeableFieldsUndefined());
    return;
  }

  // First init only
  yield put(setResubscribeableFieldsUndefinedForAppReconnection());
  yield put(resetReferencesDiscounts());
  yield put(resetReferencesPriceData());
  yield put(setInitialBasketState());
}

export function* getPendingSellerWarningResponseSaga(action: WsResponse<PendingSellerWarningResponse>): SagaIterator {
  const { pendingWarning } = action.payload;
  yield put(setPendingSellerWarning({ pendingWarning }));
}

export function* changeWebLanguageSaga({ payload }: ReturnType<typeof actions.changeWebLanguageSaga>): SagaIterator {
  const toChangeLanguage = payload.key;
  const languageUsed = yield call(changeLanguage, toChangeLanguage);

  if (languageUsed !== toChangeLanguage) {
    const usedLanguageName = Language.findInWebLang(languageUsed)?.name;
    if (usedLanguageName) {
      notifyTop(
        'warning',
        AppTranslation.t('errors.translations_not_found', 'Translations not found'),
        `Using ${usedLanguageName} as fallback language`,
      );
    }
  }

  const webLang = Language.findInWebLang(toChangeLanguage);
  logAndSetGTMLanguage(webLang);

  yield put(setWebLanguage(payload));
  yield put(setInitialInformationState());
}

export function* getAvailableDMSServicesRequestSaga({
  payload,
}: ReturnType<typeof actions.getAvailableDMSServicesRequestSaga>): SagaIterator {
  const { workshopId } = payload;
  yield put(setAvailableDMSServicesStatus({ status: LOADING }));
  yield put(sendGetAvailableDMSServicesRequest({ workshopId }));
}

export function* getAvailableDMSServicesResponseSaga(
  action: WsResponse<GetAvailableDMSServicesResponse>,
): SagaIterator {
  yield put(setAvailableDMSServices(action.payload));
}

export function* changeDocumentLanguageSaga({
  payload,
}: ReturnType<typeof actions.changeDocumentLanguageSaga>): SagaIterator {
  const { documentLanguage, documentLanguageFallback } = payload;
  yield put(setDocumentLanguages({ lang: documentLanguage, fallback: documentLanguageFallback }));
  const vehicle = yield* select(getLastVehicleDetail);
  yield call(
    fetchVehicleFunction,
    put,
    vehicle?.vehicleKey ?? '',
    false,
    vehicle?.country ?? DEFAULT_COUNTRY.key,
    vehicle?.catalogSource,
  );
  yield put(setInitialCatalogState());
  yield put(resetReferences());
}

export function* getUserSellersRequestSaga(): SagaIterator {
  yield put(setUserSellersToChoose(LOADING));
  yield put(sendGetUserSellersRequest());
}

export function* updateDhCatalogReferenceViewRequestSaga({
  payload,
}: ReturnType<typeof actions.updateDhCatalogReferenceViewRequestSaga>): SagaIterator {
  yield put(sendUpdateDhCatalogReferenceViewRequestSaga({ catalogDisplay: payload }));
}

export function* updateUserSellerSaga({ payload }: ReturnType<typeof actions.updateUserSellerSaga>): SagaIterator {
  yield put(setShowSellerPopupFalse());
  yield put(sendUpdateUserSellerRequest({ sellerId: payload }));
}

export function* addFavoriteTireBrandRequestSaga({
  payload,
}: ReturnType<typeof actions.addFavoriteTireBrandRequestSaga>): SagaIterator {
  yield put(sendAddFavoriteTireBrandForUserRequest({ tireBrand: payload.tireBrand }));
}

export function* removeFavoriteTireBrandRequestSaga({
  payload,
}: ReturnType<typeof actions.removeFavoriteTireBrandRequestSaga>): SagaIterator {
  yield put(sendRemoveFavoriteTireBrandForUserRequest({ tireBrand: payload.tireBrand }));
}

export function* resetFavoriteTireBrandRequestSaga(): SagaIterator {
  yield put(sendResetFavoriteTireBrandForUserRequest({}));
}

export function* getUserSellersResponseSaga(action: WsResponse<GetUserSellersResponse>): SagaIterator {
  const { sellers } = action.payload;
  yield put(setUserSellersToChoose(sellers));
}

export function* fetchVehicleSearchHistorySubscriptionSaga(): SagaIterator {
  yield put(setVehicleSearchHistory([]));
  yield put(sendGetVehicleHistorySubscriptionRequest());
  yield put(setVehicleSearchHistoryStatus(LOADING));
}

export function* fetchAddVehicleToSearchHistoryRequestSaga({
  payload,
}: ReturnType<typeof actions.fetchAddVehicleToSearchHistoryRequestSaga>): SagaIterator {
  const { vehicle } = payload;
  yield put(addVehicleToSearchHistoryRequest(vehicle));
}

export function* fetchRemoveVehicleFromSearchHistoryRequestSaga({
  payload,
}: ReturnType<typeof actions.fetchRemoveVehicleFromSearchHistoryRequestSaga>): SagaIterator {
  const { vehicleKey } = payload;
  yield put(sendRemoveVehicleFromSearchHistoryRequest(vehicleKey));
}

export function* fetchClearVehicleSearchHistoryRequestSaga(): SagaIterator {
  yield put(sendClearSearchHistoryRequest());
}

export function* fetchVehicleSearchHistoryResponseSaga(action: WsResponse<VehicleSearchHistoryResponse>): SagaIterator {
  const { vehicles } = action.payload;
  if (vehicles?.length > 0) {
    yield put(setVehicleSearchHistory(vehicles));
  } else {
    yield put(setVehicleSearchHistory([]));
    yield put(setVehicleSearchHistoryStatus(NOT_FOUND));
  }
}

export function* fetchUserNotifications(): SagaIterator {
  yield put(sendFetchUserNotificationsRequest());
}

export function* fetchUserNotificationsInitResponse(action: WsResponse<UserNotificationsResponse>): SagaIterator {
  yield put(setUserNotificationsInit(action.payload));
}

export function* fetchUserNotificationsUpdateResponse(action: WsResponse<UserNotificationResponse>): SagaIterator {
  yield put(setUserNotificationsUpdate(action.payload));
}

export function* cancelNotificationRequest({
  payload,
}: ReturnType<typeof actions.cancelNotificationRequest>): SagaIterator {
  yield put(sendCancelNotificationRequest(payload));
  yield put(cancelUserNotification(payload.notificationId));
}

export function* cancelAllNotificationsRequest(): SagaIterator {
  yield put(sendCancelAllNotificationsRequest());
  yield put(cancelAllUserNotifications());
}

export function* ignoreNotificationRequest({
  payload,
}: ReturnType<typeof actions.cancelNotificationRequest>): SagaIterator {
  yield put(sendIgnoreNotificationRequest(payload));
}

export function* ignoreAllNotificationsRequest(): SagaIterator {
  yield put(sendIgnoreAllNotificationsRequest());
}

export function* setupUserNotificationsRequestSaga({
  payload,
}: ReturnType<typeof actions.updateUserNotificationSettingsRequestSaga>): SagaIterator {
  yield put(sendUpdateNotificationsSettingsRequest(payload));
}

export function* setupUserNotificationsOrderStatusChangeRequestSaga({
  payload,
}: ReturnType<typeof actions.updateUserNotificationOrderStatusChangeRequestSaga>): SagaIterator {
  yield put(sendUpdateNotificationsOnOrderStatusChangeRequest(payload));
}

export function* setupUserNotificationsEstimatePeriodRequestSaga({
  payload,
}: ReturnType<typeof actions.updateUserNotificationEstimateExpirationPeriodRequestSaga>): SagaIterator {
  yield put(sendUpdateNotificationsOnEstimateExpirationRequest(payload));
}

export function* unsubscribeFromAllNotifications(): SagaIterator {
  yield put(sendUnsubscribeNotificationsRequest());
}

export function* setupOrderValidationNotificationRequestSaga({
  payload,
}: ReturnType<typeof actions.updateOrderValidationNotificationRequestSaga>): SagaIterator {
  yield put(sendUpdateOrderValidationNotificationRequest({ enabled: payload.enabled }));
}

export function* setupOrderValidationEmailNotificationRequestSaga({
  payload,
}: ReturnType<typeof actions.updateOrderValidationEmailNotificationRequestSaga>): SagaIterator {
  yield put(sendUpdateOrderValidationEmailNotificationRequest({ enabled: payload.enabled }));
}

export function* setupOrderValidationEmailAddressesRequestSaga({
  payload,
}: ReturnType<typeof actions.updateOrderValidationEmailAddressesRequestSaga>): SagaIterator {
  yield put(sendUpdateOrderValidationEmailAddressesRequest({ emailAddresses: payload.emailAddresses }));
}

export function* UsersSagas(): SagaIterator {
  yield takeEvery(actions.setCurrentUserSaga.type, sagaGuard(setCurrentUserSaga));
  yield takeEvery(actions.getUserProfileRequestSaga.type, sagaGuard(getUserProfileRequestSaga));
  yield takeEvery(actions.getUserProfileReconnectSaga.type, sagaGuard(getUserProfileReconnectSaga));
  yield takeEvery(actions.getUserProfileResponseSaga.type, sagaGuard(getUserProfileResponseSaga));
  yield takeEvery(actions.updateUserProfileLanguagesRequestSaga.type, sagaGuard(updateUserProfileLanguagesRequestSaga));
  yield takeEvery(actions.logoutUser.type, sagaGuard(logoutUser));
  yield takeEvery(actions.changeWebLanguageSaga.type, sagaGuard(changeWebLanguageSaga));
  yield takeEvery(actions.changeDocumentLanguageSaga.type, sagaGuard(changeDocumentLanguageSaga));
  yield takeEvery(actions.getAvailableDMSServicesRequestSaga.type, sagaGuard(getAvailableDMSServicesRequestSaga));
  yield takeEvery(actions.getAvailableDMSServicesResponseSaga.type, sagaGuard(getAvailableDMSServicesResponseSaga));
  yield takeEvery(actions.getUserSellersRequestSaga.type, sagaGuard(getUserSellersRequestSaga));
  yield takeEvery(actions.updateUserSellerSaga.type, sagaGuard(updateUserSellerSaga));
  yield takeEvery(actions.getUserSellersResponseSaga.type, sagaGuard(getUserSellersResponseSaga));
  yield takeEvery(actions.disconnectUser.type, sagaGuard(disconnectUser));
  yield takeEvery(actions.addFavoriteTireBrandRequestSaga.type, sagaGuard(addFavoriteTireBrandRequestSaga));
  yield takeEvery(actions.removeFavoriteTireBrandRequestSaga.type, sagaGuard(removeFavoriteTireBrandRequestSaga));
  yield takeEvery(actions.resetFavoriteTireBrandRequestSaga.type, sagaGuard(resetFavoriteTireBrandRequestSaga));
  yield takeEvery(
    actions.fetchVehicleSearchHistorySubscriptionSaga.type,
    sagaGuard(fetchVehicleSearchHistorySubscriptionSaga),
  );
  yield takeEvery(
    actions.fetchAddVehicleToSearchHistoryRequestSaga.type,
    sagaGuard(fetchAddVehicleToSearchHistoryRequestSaga),
  );
  yield takeEvery(
    actions.fetchRemoveVehicleFromSearchHistoryRequestSaga.type,
    sagaGuard(fetchRemoveVehicleFromSearchHistoryRequestSaga),
  );
  yield takeEvery(
    actions.fetchClearVehicleSearchHistoryRequestSaga.type,
    sagaGuard(fetchClearVehicleSearchHistoryRequestSaga),
  );
  yield takeEvery(actions.fetchVehicleSearchHistoryResponseSaga.type, sagaGuard(fetchVehicleSearchHistoryResponseSaga));
  yield takeEvery(actions.reloadSubscriptionBeUserSaga.type, sagaGuard(reloadSubscriptionBeUserSaga));
  yield takeEvery(
    actions.updateDhCatalogReferenceViewRequestSaga.type,
    sagaGuard(updateDhCatalogReferenceViewRequestSaga),
  );
  yield takeEvery(actions.getPendingSellerWarningResponseSaga.type, sagaGuard(getPendingSellerWarningResponseSaga));
  yield takeEvery(actions.fetchUserNotificationsRequest.type, sagaGuard(fetchUserNotifications));
  yield takeEvery(actions.fetchUserNotificationsInitResponse.type, sagaGuard(fetchUserNotificationsInitResponse));
  yield takeEvery(actions.fetchUserNotificationsUpdateResponse.type, sagaGuard(fetchUserNotificationsUpdateResponse));
  yield takeEvery(actions.cancelNotificationRequest.type, sagaGuard(cancelNotificationRequest));
  yield takeEvery(actions.cancelAllNotificationsRequest.type, sagaGuard(cancelAllNotificationsRequest));
  yield takeEvery(actions.ignoreNotificationRequest.type, sagaGuard(ignoreNotificationRequest));
  yield takeEvery(actions.ignoreAllNotificationsRequest.type, sagaGuard(ignoreAllNotificationsRequest));

  yield takeEvery(actions.updateUserNotificationSettingsRequestSaga.type, sagaGuard(setupUserNotificationsRequestSaga));
  yield takeEvery(
    actions.updateUserNotificationOrderStatusChangeRequestSaga.type,
    sagaGuard(setupUserNotificationsOrderStatusChangeRequestSaga),
  );
  yield takeEvery(
    actions.updateUserNotificationEstimateExpirationPeriodRequestSaga.type,
    sagaGuard(setupUserNotificationsEstimatePeriodRequestSaga),
  );
  yield takeEvery(actions.unsubscribeFromAllNotificationsRequest.type, sagaGuard(unsubscribeFromAllNotifications));
  yield takeEvery(
    actions.updateOrderValidationNotificationRequestSaga.type,
    sagaGuard(setupOrderValidationNotificationRequestSaga),
  );
  yield takeEvery(
    actions.updateOrderValidationEmailNotificationRequestSaga.type,
    sagaGuard(setupOrderValidationEmailNotificationRequestSaga),
  );
  yield takeEvery(
    actions.updateOrderValidationEmailAddressesRequestSaga.type,
    sagaGuard(setupOrderValidationEmailAddressesRequestSaga),
  );
}
